import React, { Component, useState } from 'react';
import { List, PageHeader, Card, Tag, Typography, Button, Space, Avatar } from 'antd';
import { 
  UserOutlined, 
  BankOutlined, 
  ThunderboltOutlined, 
  ClockCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ExportOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  TeamOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import moment from 'moment';
import '../../../App.scss';
import 'antd/dist/antd.css';

import useNotifications from '../../../models/useNotifications';
import { JSONParagraph } from '../../../components/admin/JSONParagraph';

const { Text } = Typography;

const getVisualsForType = (type) => {
  switch (type) {
    case 'client':
      return { icon: <UserOutlined />, color: 'cyan', label: 'Client' };
    case 'welcomeEmail':
      return { icon: <MailOutlined />, color: 'cyan', label: 'Welcome' };
    case 'inviteDelegateUser':
      return { icon: <MailOutlined />, color: 'blue', label: 'Invite' };
    case 'joinedTeam':
      return { icon: <TeamOutlined />, color: 'cyan', label: 'Team Join' };
    case 'account':
      return { icon: <BankOutlined />, color: 'blue', label: 'Account' };
    case 'withdrawal':
      return { icon: <ExportOutlined />, color: 'red', label: 'Withdrawal' };
    case 'invoice-charged':
      return { icon: <FileTextOutlined />, color: 'blue', label: 'Invoice' };
    case 'teamTransfer':
      return { icon: <ExportOutlined />, color: 'green', label: 'Transfer' };
    case 'refundIssued':
      return { icon: <WarningOutlined />, color: 'volcano', label: 'Refund' };
    case 'paymentConfirmation':
    case 'payerPaymentSuccess':
    case 'tipConfirmation':
    case 'payerTipSuccess':
    case 'donorRecipientConfirmation':
    case 'donorPaymentSuccess':
    case 'tap':
    case 'tap_legacy':
      return { icon: <ThunderboltOutlined />, color: 'gold', label: 'Tap' };
    case 'tag':
    case 'tag_claimed':
      return { icon: <ThunderboltOutlined />, color: 'blue', label: 'Tag' };
    case 'kycVerified':
    case 'idVerificationStatusChanged':
      return { icon: <CheckCircleOutlined />, color: 'green', label: 'Identity' };
    case 'customerorder':
      return { icon: <FileTextOutlined />, color: 'orange', label: 'Order' };
    default:
      return { icon: <InfoCircleOutlined />, color: 'default', label: type || 'Info' };
  }
};

const NotificationItem = ({ notification }) => {
  const [expanded, setExpanded] = useState(false);
  const { item, id } = notification;
  
  // Derive type if missing based on content
  let effectiveType = item.type;
  if (!effectiveType) {
    if (item.tag) effectiveType = 'tag';
    else if (item.tap) effectiveType = 'tap';
  }

  const { icon, color, label } = getVisualsForType(effectiveType);
  
  const createdAt = item.createdAt ? moment(item.createdAt.toMillis()).format("YYYY-MM-DD HH:mm:ss") : 'N/A';

  const renderAvatar = () => {
    const client = item.client || {};
    const tag = item.tag || item.tap || {};
    
    // Priority 1: Client Avatar (Circle)
    if (client.avatar || item.avatar) {
      return <Avatar src={client.avatar || item.avatar} shape="circle" size={40} icon={<UserOutlined />} />;
    }
    
    // Priority 2: Client Top Logo (Square)
    if (client.topLogo || item.topLogo) {
      return <Avatar src={client.topLogo || item.topLogo} shape="square" size={40} icon={<UserOutlined />} />;
    }
    
    // Priority 3: Tag/Tap Avatar (Circle)
    if (tag.avatar) {
      return <Avatar src={tag.avatar} shape="circle" size={40} icon={<UserOutlined />} />;
    }

    // Default: Placeholder icon with theme color
    return <Avatar shape="circle" size={40} icon={icon} style={{ backgroundColor: color }} />;
  };

  const displayName = item.client?.displayName || item.displayName || item.tap?.displayName || item.tag?.displayName || item.tap?.customerName;

  return (
    <Card 
      size="small" 
      style={{ 
        marginBottom: '12px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        borderLeft: `4px solid ${color}`,
        backgroundColor: '#fff'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '12px', flex: 1, textAlign: 'left' }}>
          <div style={{ paddingTop: '4px' }}>
            {renderAvatar()}
          </div>
          <Space direction="vertical" size={2} style={{ flex: 1 }}>
            <Space size={8} wrap>
              <Tag color={color} icon={icon} style={{ borderRadius: '4px', fontWeight: 'bold' }}>
                {label.toUpperCase()}
              </Tag>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                {createdAt}
              </Text>
              {item.mode === false && <Tag color="orange" size="small">TEST</Tag>}
            </Space>
            <div style={{ marginTop: '4px' }}>
              <Text strong style={{ fontSize: '14px' }}>{item.text || 'Notification'}</Text>
            </div>
            <Space size={16} style={{ marginTop: '2px' }} wrap>
              {item.clientId && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <span style={{ fontWeight: '600' }}>Client:</span> <a href={`/admin/clients/${item.clientId}`}>{item.clientId}</a>
                  {displayName && (
                    <span style={{ marginLeft: '4px' }}>({displayName})</span>
                  )}
                </Text>
              )}
              {(item.teamName || item.team?.teamName) && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <span style={{ fontWeight: '600' }}>Team:</span> {item.teamName || item.team?.teamName}
                </Text>
              )}
              {item.target && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <span style={{ fontWeight: '600' }}>Target:</span> {item.target}
                </Text>
              )}
              {item.status && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <span style={{ fontWeight: '600' }}>Status:</span> {item.status}
                </Text>
              )}
            </Space>
          </Space>
        </div>
        <Button 
          type="link" 
          size="small" 
          icon={expanded ? <EyeInvisibleOutlined /> : <EyeOutlined />} 
          onClick={() => setExpanded(!expanded)}
          style={{ padding: 0 }}
        >
          {expanded ? 'Hide JSON' : 'Raw JSON'}
        </Button>
      </div>
      {expanded && (
        <div style={{ 
          marginTop: '12px', 
          padding: '12px', 
          backgroundColor: '#f9f9f9', 
          borderRadius: '4px', 
          border: '1px solid #f0f0f0',
          textAlign: 'left' 
        }}>
          <JSONParagraph item={notification} />
        </div>
      )}
    </Card>
  );
};

const AdminNotificationsScreenBody = () => {
  const notifications = useNotifications(null);
  const [pageSize, setPageSize] = useState(20);

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <List
          loading={!notifications}
          size='small'
          pagination={{ 
            pageSize, 
            showSizeChanger: true, 
            onShowSizeChange: (current, size) => setPageSize(size),
            position: 'both' 
          }}
          dataSource={notifications || []}
          renderItem={notification => (
            <NotificationItem key={`not-li-${notification.id}`} notification={notification} />
          )}
        />
    </div>
  );
};


// /admin/notifictions
export default class AdminNotificationsScreen extends Component {

  constructor(props) {
    super(props);
    this.model = null;

    this.state = {
      lastUpdatedId: null,
      notifications: null,
      loading: true,
    };
  }

  componentDidMount() {
  }

  componentWillUnmount() {
    if (this.model) this.model.stop();
  }

  componentDidUpdate() {
  }

  isLoading = () => {
    return this.state.loading || false;
  }


  render() {

    return (
      <div style={{ height: '100%', textAlign: 'center' }}>
        <PageHeader
          ghost={false}
          title={`Notifications`}
        />
        <AdminNotificationsScreenBody />
      </div>
    );
  }

}

